// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- Actions & Hooks
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useJobsActions } from 'datastage/store/jobs/useJobs';
import { useJobMetricActions } from 'datastage/store/jobMetrics/useJobMetrics';
import { useJobsData } from './hooks/useJobsData';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
import { Col, Row } from 'antd';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { JobsGrid } from './components/grid';
import { JobsSearchForm } from './components/search-form';
import { TwoRowsLayout } from './layout/two-row';
// --- Utilities
import { datastageApi } from 'datastage/api';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';

/**
 * Jobs Page
 *
 * Features:
 * - Displays a paginated list of jobs
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Responds to refreshJobs flag from Redux for grid refresh
 * - Persists page size preference to localStorage automatically
 *
 * @returns {JSX.Element}
 */
export const JobsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { fetchJobs, updateJobs, itemsPerPage, jobRange, filterForm, jobSort } = useJobsData();
    const { updateJobMetrics } = useJobMetricActions();
    const { updateJobs: updateJobsAction } = useJobsActions();

    const { totalPages, currentPage, totalItems, refreshJobs, pageSizeOptions } = useSelector((state) => state.jobs);

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.jobs,
        itemsPerPage,
        rangeKey: 'jobRange',
        onSync: updateJobs,
    });

    // Handle refreshJobs flag changes
    useEffect(() => {
        if (refreshJobs) {
            (async () => {
                const data = await fetchJobs(jobRange);
                if (data) {
                    updateJobs({
                        jobs: data?.content ?? [],
                        totalPages: data?.totalPages ?? 0,
                        currentPage: data?.pageable?.pageNumber ?? 0,
                        totalItems: data?.totalElements ?? 0,
                        refreshJobs: false,
                    });
                } else {
                    // Reset flag even on error
                    updateJobsAction({ refreshJobs: false });
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshJobs]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;
        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchJobs(nextPageRange);
        if (data) {
            updateJobs({
                jobs: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                jobRange: nextPageRange,
                totalItems: data?.totalElements ?? 0,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    // Refresh the jobs list
    const handleRefresh = async () => {
        const data = await fetchJobs(jobRange);
        if (data) {
            updateJobs({
                jobs: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
            });
        }
    };

    /**
     * Export all jobs to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        try {
            setLoadingTrue();
            const exportRange = getAllItemsRange(totalItems);
            const data = await datastageApi.jobs(
                filterCriteria(filterForm, '', 'jobs'),
                exportRange,
                jobSort,
            );

            if (data?.code) {
                console.error('Failed to export jobs data:', data?.message);
                return;
            }

            const exportFields = [
                'id',
                'spaJobType',
                'spaJobStatus',
                'startedByUser',
                'targetCount',
                'progressCount',
                'errorCount',
                'createdAt',
                'modifiedAt',
                'completedAt',
            ];
            const filteredContent = data?.content?.map((item) =>
                exportFields.reduce((obj, key) => {
                    obj[key] = item[key];
                    return obj;
                }, {}),
            );

            exportToCsv(filteredContent, 'jobs');
        } catch (error) {
            console.error('Error exporting jobs:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const openJobMetricsModal = () => {
        updateJobMetrics({
            isModalOpen: true,
        });
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Jobs
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        iconType="fa-solid fa-chart-line"
                        labelText="metrics"
                        styleType="link"
                        onClick={openJobMetricsModal}
                    >
                        Job Metrics
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>
            <TwoRowsLayout>
                <main data-position="top">
                    <JobsSearchForm />
                </main>
                <main data-position="bottom">
                    <JobsGrid />
                </main>
            </TwoRowsLayout>
            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />
            <ToastNotificationProvider />
        </>
    );
};
